import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT, type Clave } from '../i18n/index.js';
import {
  abrirCamara,
  clasificarFalla,
  crearDetector,
  hayCamara,
  ponerLinterna,
  tieneLinterna,
  type Detector,
  type FallaCamara,
  type Motor,
} from '../lib/escaner.js';
import { aceptarLectura, crearFiltroLecturas, recorteDelVisor, type Lectura } from '../lib/lecturas.js';
import { pitar, prepararSonido, type Resultado } from '../lib/pitido.js';
import { Ico } from './Iconos.js';

/** Widest picture handed to the reader. Past this a cheap phone only reads slower, not better. */
const ANCHO_MAX = 960;
/** From the start of one read to the start of the next: about ten a second, at most. */
const INTERVALO_MS = 90;
/**
 * The same, once nothing has been in the box for `REPOSO_TRAS_MS`. The till's camera can stay
 * open for a whole shift; reading an empty counter ten times a second is a phone running hot for
 * nothing. The first barcode back in view puts it straight back to full speed.
 */
const INTERVALO_REPOSO_MS = 250;
const REPOSO_TRAS_MS = 10_000;
/** The phone's own reader failing this many frames running is a reader that does not work here. */
const FALLOS_ANTES_DE_CAMBIAR = 5;

type Fase = 'abriendo' | 'preparando' | 'leyendo' | FallaCamara;

const FALLA: Record<FallaCamara, [Clave, Clave]> = {
  permiso: ['escaner.falla.permiso', 'escaner.falla.permisoAyuda'],
  sinCamara: ['escaner.falla.sinCamara', 'escaner.falla.sinCamaraAyuda'],
  ocupada: ['escaner.falla.ocupada', 'escaner.falla.ocupadaAyuda'],
  lector: ['escaner.falla.lector', 'escaner.falla.lectorAyuda'],
  otra: ['escaner.falla.otra', 'escaner.falla.otraAyuda'],
};

export function useHayCamara(): boolean {
  const [hay, setHay] = useState(false);
  useEffect(() => {
    let vivo = true;
    void hayCamara().then((si) => {
      if (vivo) setHay(si);
    });
    return () => {
      vivo = false;
    };
  }, []);
  return hay;
}

/**
 * The button beside a field that opens the camera. Absent on a device with no camera.
 *
 * The same square wherever it appears, so it is learnt once. It also readies the beep: the tap
 * is the one moment a browser lets a page start making sound.
 */
export function BotonEscanear({ onClick }: { onClick: () => void }) {
  const t = useT();
  const camara = useHayCamara();
  if (!camara) return null;
  return (
    <button
      type="button"
      className="btn-secundario size-14 min-h-0 shrink-0 p-0"
      aria-label={t('escaner.abrir')}
      title={t('escaner.abrir')}
      onClick={() => {
        prepararSonido();
        onClick();
      }}
    >
      <Ico icono="escanear" className="size-6" />
    </button>
  );
}

/**
 * La cámara, leyendo códigos de barras.
 *
 * Full screen, because the picture is how somebody aims: a barcode has to be inside the box to
 * count, and a box the size of a thumbnail is a box nobody can hit with one hand. Only what is
 * inside it is read (`recorteDelVisor`), so with three products on the counter the one in the
 * box is the one that rings up.
 *
 * It knows nothing about products. It hands each code that counts to `onLeido`, which answers
 * whether that was the right thing — that picks the beep and the colour the box flashes — and
 * the screen that opened it decides everything else. A `hoja` is a question laid over the
 * picture, and nothing is read while one is up; the code that raised it does not count again
 * when it goes away, or the same can still in front of the lens would ask the same question
 * the moment it was answered.
 *
 * The camera stops the instant this closes, and closes when the app goes to the background: a
 * phone whose camera light stays on in somebody's pocket is a phone they stop trusting.
 */
export function Escaner({
  titulo,
  onLeido,
  onCerrar,
  pausado = false,
  hoja,
  pie,
}: {
  titulo: string;
  onLeido: (lectura: Lectura) => Resultado;
  onCerrar: () => void;
  /** Nothing is read while this is set: the catalogue is still loading, say. */
  pausado?: boolean;
  hoja?: ReactNode;
  /** Under the picture: what the last read did, and whatever the screen wants beside it. */
  pie?: ReactNode;
}) {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const visor = useRef<HTMLDivElement>(null);
  const flujo = useRef<MediaStream | null>(null);
  const [fase, setFase] = useState<Fase>('abriendo');
  const [intento, setIntento] = useState(0);
  const [linterna, setLinterna] = useState<boolean | null>(null);
  const [destello, setDestello] = useState<Resultado | null>(null);

  // The reading loop outlives any one render, so it reads these instead of closing over props.
  const alLeer = useRef(onLeido);
  alLeer.current = onLeido;
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;
  const enPausa = useRef(false);
  enPausa.current = pausado || Boolean(hoja);

  useEffect(() => {
    let vivo = true;
    let temporizador = 0;
    let stream: MediaStream | null = null;
    const filtro = crearFiltroLecturas();
    const lienzo = document.createElement('canvas');
    const pincel = lienzo.getContext('2d', { willReadFrequently: true });
    setFase('abriendo');
    setLinterna(null);

    function apagar() {
      stream?.getTracks().forEach((pista) => pista.stop());
      stream = null;
      flujo.current = null;
    }

    let ultimaVista = performance.now();

    /** The part of the current frame under the box, read. */
    async function leerCuadro(detector: Detector): Promise<Lectura | null> {
      const v = video.current;
      const caja = visor.current;
      if (!v || !caja || !pincel || v.readyState < v.HAVE_CURRENT_DATA || !v.videoWidth) return null;
      const rv = v.getBoundingClientRect();
      const rc = caja.getBoundingClientRect();
      const r = recorteDelVisor(
        { ancho: v.videoWidth, alto: v.videoHeight },
        { ancho: rv.width, alto: rv.height },
        { x: rc.left - rv.left, y: rc.top - rv.top, ancho: rc.width, alto: rc.height },
      );
      if (r.ancho < 32 || r.alto < 16) return null;

      const escala = Math.min(1, ANCHO_MAX / r.ancho);
      const ancho = Math.round(r.ancho * escala);
      const alto = Math.round(r.alto * escala);
      if (lienzo.width !== ancho) lienzo.width = ancho;
      if (lienzo.height !== alto) lienzo.height = alto;
      pincel.drawImage(v, r.x, r.y, r.ancho, r.alto, 0, 0, ancho, alto);

      const detectados = await detector.detect(lienzo);
      if (detectados.length > 0) ultimaVista = performance.now();
      for (const d of detectados) {
        const lectura = aceptarLectura(d.rawValue, d.format);
        if (lectura) return lectura;
      }
      return null;
    }

    async function arrancar() {
      // The reader compiles while the camera warms up; neither waits for the other.
      const preparado = crearDetector();
      preparado.catch(() => undefined);

      try {
        stream = await abrirCamara();
      } catch (e) {
        if (vivo) setFase(clasificarFalla(e));
        return;
      }
      if (!vivo) return apagar();
      flujo.current = stream;

      const v = video.current;
      if (v) {
        v.muted = true;
        v.srcObject = stream;
        await v.play().catch(() => undefined);
      }
      if (!vivo) return;
      setLinterna(tieneLinterna(stream) ? false : null);
      setFase('preparando');

      let detector: Detector;
      let motor: Motor;
      try {
        ({ detector, motor } = await preparado);
      } catch {
        if (vivo) setFase('lector');
        return;
      }
      if (!vivo) return;
      setFase('leyendo');

      let fallos = 0;
      let ultimo: string | null = null;
      let pausaba = false;

      const vuelta = async () => {
        if (!vivo) return;
        const inicio = performance.now();

        if (enPausa.current) {
          pausaba = true;
        } else {
          if (pausaba) {
            // Somebody just answered a question and is about to scan again: full speed.
            ultimaVista = inicio;
            if (ultimo) filtro.silenciar(ultimo, inicio);
          }
          pausaba = false;
          try {
            const lectura = await leerCuadro(detector);
            fallos = 0;
            if (lectura && vivo && !enPausa.current && filtro.leer(lectura.codigo, performance.now())) {
              ultimo = lectura.codigo;
              const resultado = alLeer.current(lectura);
              pitar(resultado);
              setDestello(resultado);
            }
          } catch {
            // A frame the reader choked on is only a frame. The phone's own reader choking on
            // every one is a reader that does not work on this phone, and the bundled one
            // takes over without anybody having to know there were two.
            if (motor === 'nativo' && ++fallos >= FALLOS_ANTES_DE_CAMBIAR) {
              try {
                ({ detector, motor } = await crearDetector('wasm'));
                fallos = 0;
              } catch {
                if (vivo) setFase('lector');
                return;
              }
            }
          }
        }

        const intervalo = inicio - ultimaVista > REPOSO_TRAS_MS ? INTERVALO_REPOSO_MS : INTERVALO_MS;
        if (vivo) temporizador = window.setTimeout(vuelta, Math.max(0, intervalo - (performance.now() - inicio)));
      };
      void vuelta();
    }

    void arrancar();
    return () => {
      vivo = false;
      window.clearTimeout(temporizador);
      apagar();
    };
  }, [intento]);

  useEffect(() => {
    const alOcultar = () => {
      if (document.hidden) cerrar.current();
    };
    document.addEventListener('visibilitychange', alOcultar);
    return () => document.removeEventListener('visibilitychange', alOcultar);
  }, []);

  useEffect(() => {
    if (!destello) return;
    const id = window.setTimeout(() => setDestello(null), 450);
    return () => window.clearTimeout(id);
  }, [destello]);

  async function cambiarLinterna() {
    if (!flujo.current || linterna === null) return;
    try {
      await ponerLinterna(flujo.current, !linterna);
      setLinterna(!linterna);
    } catch {
      setLinterna(null);
    }
  }

  const falla = fase === 'abriendo' || fase === 'preparando' || fase === 'leyendo' ? null : fase;
  const mensaje =
    fase === 'abriendo'
      ? t('escaner.abriendo')
      : fase === 'preparando'
        ? t('escaner.preparando')
        : fase === 'leyendo'
          ? t('escaner.ayuda')
          : null;

  return createPortal(
    // `touch-none` keeps a thumb dragging across the picture from scrolling the page behind it;
    // the parts that do scroll say so themselves.
    //
    // Keys stop here. This is rendered into <body> but React still bubbles its events up the
    // component tree, into whatever screen opened it — where Enter saves a row and Escape
    // clears one. Focus starts on «Cerrar», so every key pressed while it is open lands here.
    <div
      className="aparece fixed inset-0 z-[60] flex touch-none flex-col bg-black text-slate-100 outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      tabIndex={-1}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape') onCerrar();
      }}
    >
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <video ref={video} className="absolute inset-0 size-full object-cover" muted playsInline autoPlay />

        {!falla && (
          // Everything outside the box is dimmed by its own shadow, so the box is the bright
          // part of the screen and the eye goes there without being told. The frame is a
          // border, not a `ring`: a ring is a box-shadow too, and the dimming would replace it.
          <div
            ref={visor}
            className={`absolute top-1/2 left-1/2 aspect-[2/1] w-[min(82%,26rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border-2 ${
              destello === 'bien' ? 'border-emerald-400' : destello === 'mal' ? 'border-amber-400' : 'border-white/80'
            }`}
            style={{ boxShadow: '0 0 0 100vmax rgb(0 0 0 / 0.5)' }}
          >
            <span className="absolute inset-x-4 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-rose-500 shadow-[0_0_10px] shadow-rose-500/80" />
          </div>
        )}

        {/* Before the header, so «Cerrar» stays on top of it: a camera that failed must still close. */}
        {falla && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <span className="chip chip-ambar size-16 rounded-2xl">
              <Ico icono="aviso" className="size-8" />
            </span>
            <p className="max-w-sm text-lg font-semibold">{t(FALLA[falla][0])}</p>
            <p className="max-w-sm text-sm text-slate-400">{t(FALLA[falla][1])}</p>
            <button type="button" className="btn-primario" onClick={() => setIntento((n) => n + 1)}>
              <Ico icono="reiniciar" />
              {t('escaner.reintentar')}
            </button>
          </div>
        )}

        <header className="absolute inset-x-0 top-0 flex items-center gap-2 bg-linear-to-b from-black/80 to-transparent px-3 pt-[max(env(safe-area-inset-top),0.75rem)] pb-8">
          <button
            type="button"
            onClick={onCerrar}
            aria-label={t('comun.cerrar')}
            className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-black/40 text-white transition hover:bg-black/60"
            autoFocus
          >
            <Ico icono="cerrar" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-center text-base font-semibold">{titulo}</h2>
          {linterna !== null ? (
            <button
              type="button"
              onClick={() => void cambiarLinterna()}
              aria-label={t('escaner.linterna')}
              aria-pressed={linterna}
              className={`flex size-12 shrink-0 items-center justify-center rounded-xl transition ${
                linterna ? 'bg-amber-300 text-slate-950 hover:bg-amber-200' : 'bg-black/40 text-white hover:bg-black/60'
              }`}
            >
              <Ico icono="linterna" />
            </button>
          ) : (
            <span className="size-12 shrink-0" aria-hidden />
          )}
        </header>

        {mensaje && (
          <p className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/80 to-transparent px-6 pt-10 pb-5 text-center text-sm font-medium text-white">
            {mensaje}
          </p>
        )}

        {hoja && (
          <div className="surge absolute inset-0 z-10 flex touch-pan-y flex-col overflow-y-auto overscroll-contain bg-slate-950">
            {hoja}
          </div>
        )}
      </div>

      {pie && (
        <div className="border-t border-white/10 bg-slate-950 px-4 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {pie}
        </div>
      )}
    </div>,
    document.body,
  );
}
